/**
 * Lazy Google Maps JavaScript API loader.
 *
 * - Loads the Maps JS once per browser session, cached on `window`.
 * - Returns a promise that resolves to the global `google.maps` namespace.
 * - Surfaces a typed `MapsLoadError` if the API key is unset or the script
 *   fails to load (network / rate-limit / referer-restriction misconfigured).
 *
 * The browser key is build-time injected as `REACT_APP_GOOGLE_MAPS_BROWSER_KEY`
 * in `bitbucket-pipelines.yml`. It is intentionally separate from the
 * backend's `GOOGLE_MAPS_API_KEY` because the browser key needs HTTP-referer
 * restrictions configured in Google Cloud Console for the scanner hosts.
 */

export class MapsLoadError extends Error {
    code: 'NO_KEY' | 'SCRIPT_ERROR' | 'TIMEOUT';
    constructor(message: string, code: MapsLoadError['code']) {
        super(message);
        this.code = code;
    }
}

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        google?: any;
        __kkMapsLoaderPromise?: Promise<unknown>;
    }
}

let loaderPromise: Promise<unknown> | null = null;

export const isGoogleMapsKeyConfigured = (): boolean => {
    return !!process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY;
};

export const loadGoogleMaps = (): Promise<unknown> => {
    if (typeof window === 'undefined') {
        return Promise.reject(new MapsLoadError('No window context', 'SCRIPT_ERROR'));
    }
    if (window.google?.maps) {
        return Promise.resolve(window.google.maps);
    }
    if (loaderPromise) return loaderPromise;
    if (window.__kkMapsLoaderPromise) {
        loaderPromise = window.__kkMapsLoaderPromise;
        return loaderPromise;
    }

    const key = process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY;
    if (!key) {
        return Promise.reject(
            new MapsLoadError(
                'Google Maps browser key is not configured (REACT_APP_GOOGLE_MAPS_BROWSER_KEY).',
                'NO_KEY'
            )
        );
    }

    loaderPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.async = true;
        script.defer = true;
        script.src =
            `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
            `&libraries=geometry&v=weekly`;
        script.onload = () => {
            if (window.google?.maps) {
                resolve(window.google.maps);
            } else {
                reject(new MapsLoadError('Google Maps loaded but namespace missing', 'SCRIPT_ERROR'));
            }
        };
        script.onerror = () => {
            // Allow a retry next time the user navigates to the map tab.
            loaderPromise = null;
            window.__kkMapsLoaderPromise = undefined;
            reject(new MapsLoadError('Failed to load Google Maps script', 'SCRIPT_ERROR'));
        };
        document.head.appendChild(script);
    });

    window.__kkMapsLoaderPromise = loaderPromise;
    return loaderPromise;
};
